/**
 * Pure utilities for speaker ranking and speaker-to-face resolution.
 *
 * These functions bridge the gap between transcript speaker labels and
 * face detection labels, enabling speaker-aware cluster selection during
 * auto-framing.
 *
 * Mapping chain:
 *   speaker_label → (SpeakerVoiceMapping.mappings) → speakerId
 *   face_label    → (SpeakerIdentifications)       → speakerId
 *
 * By reversing SpeakerIdentifications we get speakerId → face_label,
 * which composes with the voice mapping to give speaker_label → face_label.
 *
 * @module speakerRanking
 */

import { LogManager } from '@src/utility/Logger';

import type { FaceTracks, SpeakerIdentifications } from './types';

const logger = LogManager.getLogger('framing:speakerRanking');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal transcript word shape needed for speaker ranking. */
export type TranscriptWordForRanking = {
  ts?: number | null;
  end_ts?: number | null;
  speaker_label?: string;
  type?: string;
};

/** A speaker ranked by total speaking duration within a scene. */
export type RankedSpeaker = {
  speakerLabel: string;
  /** Total duration in seconds this speaker occupies in the scene. */
  totalDuration: number;
};

/**
 * Shape of the SpeakerVoiceMapping.mappings field.
 * Maps speaker_label → Dato speaker ID.
 */
export type SpeakerLabelToIdMappings = Record<string, string>;

// ---------------------------------------------------------------------------
// buildSpeakerLabelToFaceLabelsMap
// ---------------------------------------------------------------------------

/**
 * Composes speaker_label → face_labels by joining `voiceMappings`
 * (speaker_label → speakerId) with the inverse of `speakerIdentifications`
 * (face_label → speakerId).
 *
 * A speaker gets one `face_label` per track the identifier assigned to them —
 * multi-camera footage tracks the same person separately in each shot (close-up
 * vs. wide), and keeping only one would leave the other shots unmatched.
 * Labels are ordered by detection count in `faceTracks`, most first; ties keep
 * the first-iterated face for determinism.
 */
export function buildSpeakerLabelToFaceLabelsMap(
  voiceMappings: SpeakerLabelToIdMappings,
  speakerIdentifications: SpeakerIdentifications,
  faceTracks: FaceTracks,
): Record<string, string[]> {
  const facesPerSpeakerId = new Map<string, string[]>();
  for (const [faceLabel, speakerId] of Object.entries(speakerIdentifications)) {
    const list = facesPerSpeakerId.get(speakerId);
    if (list) {
      list.push(faceLabel);
    } else {
      facesPerSpeakerId.set(speakerId, [faceLabel]);
    }
  }

  for (const faceLabels of facesPerSpeakerId.values()) {
    faceLabels.sort(
      (a, b) => (faceTracks[b]?.length ?? 0) - (faceTracks[a]?.length ?? 0),
    );
  }

  const result: Record<string, string[]> = {};
  for (const [speakerLabel, speakerId] of Object.entries(voiceMappings)) {
    const faceLabels = facesPerSpeakerId.get(speakerId);
    if (faceLabels) {
      result[speakerLabel] = [...faceLabels];
    }
  }

  logger.debug(
    `buildSpeakerLabelToFaceLabelsMap: ` +
      `voiceMappings=${Object.keys(voiceMappings).length}, ` +
      `identifications=${Object.keys(speakerIdentifications).length} → ` +
      `resolved=${Object.keys(result).length} speaker→face links`,
  );

  return result;
}

// ---------------------------------------------------------------------------
// rankSpeakersForScene
// ---------------------------------------------------------------------------

/**
 * Ranks speakers by total speaking duration within a time range.
 *
 * Counts the cumulative word duration for each `speaker_label` that overlaps
 * [sceneStart, sceneEnd). Returns speakers sorted descending by total duration.
 *
 * @param transcriptElements - Full transcript words (with ts, end_ts, speaker_label)
 * @param sceneStart - Scene start time (in same time domain as word timestamps)
 * @param sceneEnd - Scene end time
 * @returns Ranked speakers, longest-speaking first
 */
export function rankSpeakersForScene(
  transcriptElements: ReadonlyArray<TranscriptWordForRanking>,
  sceneStart: number,
  sceneEnd: number,
): RankedSpeaker[] {
  const durationBySpeaker = new Map<string, number>();

  for (const el of transcriptElements) {
    if (el.type !== 'text' || el.ts == null || el.end_ts == null) continue;
    if (!el.speaker_label) continue;

    // Clip word to scene range
    const wordStart = Math.max(el.ts, sceneStart);
    const wordEnd = Math.min(el.end_ts, sceneEnd);
    if (wordStart >= wordEnd) continue;

    const duration = wordEnd - wordStart;
    const prev = durationBySpeaker.get(el.speaker_label) ?? 0;
    durationBySpeaker.set(el.speaker_label, prev + duration);
  }

  const ranked: RankedSpeaker[] = [];
  for (const [speakerLabel, totalDuration] of durationBySpeaker) {
    ranked.push({ speakerLabel, totalDuration });
  }

  ranked.sort((a, b) => b.totalDuration - a.totalDuration);
  return ranked;
}
